/**
 * GPU Reservation Modal Component
 * Allows users to configure and reserve a GPU instance
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  ArrowLeftIcon,
  CpuChipIcon,
  ServerIcon,
  CircleStackIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  InformationCircleIcon,
  KeyIcon,
  CheckIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../services/marketplaceApi';
import { sshKeyService, projectService, currencyService } from '../../services/api';
import { InventoryResponse, OSImageResponse, CreateOrderRequest, CreateOrderResponse, ReservationResponse } from '../../types/marketplace';
import { SSHKey } from '../../types';
import CreateSSHKeyModal from '../pods/CreateSSHKeyModal';
import ProvisioningModal from './ProvisioningModal';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { loadRazorpayScript } from '../../utils/razorpay';
import toast from 'react-hot-toast';

interface ReservationModalProps {
  item: InventoryResponse;
  onClose: () => void;
  onSuccess?: () => void;
}

interface DiskConfig {
  id: string;
  type: string;
  diskCount: number;
  sizePerDisk: string;
  layout: string;
  format: string;
  mountpoint: string;
}

const diskLayouts = ['lvm', 'raid0', 'raid1', 'single'];
const diskFormats = ['ext4', 'xfs', 'btrfs'];

const ReservationModal: React.FC<ReservationModalProps> = ({ item, onClose, onSuccess }) => {
  const queryClient = useQueryClient();

  // Use the global selected project hook
  const {
    selectedProjectId,
    projects,
    projectsLoading,
    setSelectedProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Fetch OS images from API
  const { data: osImagesData, isLoading: osImagesLoading } = useQuery({
    queryKey: ['os-images'],
    queryFn: () => marketplaceApi.listOSImages(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  const osImages = osImagesData?.data || [];

  // Fetch SSH keys from API
  const { data: sshKeysData, isLoading: sshKeysLoading } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => sshKeyService.list(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const sshKeys: SSHKey[] = sshKeysData?.data?.data || sshKeysData?.data || [];

  // Fetch currency exchange rate
  const { data: currencyData } = useQuery({
    queryKey: ['currency-rates'],
    queryFn: () => currencyService.get().then((res) => res.data),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // State for SSH key modal
  const [showCreateSSHKeyModal, setShowCreateSSHKeyModal] = useState(false);

  // State for create project
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');

  // State for payment flow
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showProvisioningModal, setShowProvisioningModal] = useState(false);
  const [verifiedReservation, setVerifiedReservation] = useState<ReservationResponse | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Feature flags for payment methods
  const exchangeRate = currencyData?.usd_to_inr || 92; // Fetched from backend, fallback to 92

  // Find the default OS or use first one
  const defaultOS = osImages.find((os: OSImageResponse) => os.isDefault)?.slug || osImages[0]?.slug || 'ubuntu-jammy-vanilla';

  // Generate disk configs based on storage
  const generateDiskConfigs = (): DiskConfig[] => {
    const totalStorage = item.configuration.storageGB;
    const configs: DiskConfig[] = [];

    if (totalStorage > 1000) {
      const dataDiskSize = totalStorage * 0.8;
      configs.push({
        id: 'nvme-data',
        type: 'NVME',
        diskCount: 8,
        sizePerDisk: `${(dataDiskSize / 8 / 1024).toFixed(2)} TB`,
        layout: 'lvm',
        format: 'ext4',
        mountpoint: '/data0',
      });

      configs.push({
        id: 'nvme-system',
        type: 'NVME',
        diskCount: 2,
        sizePerDisk: `${((totalStorage * 0.2) / 2).toFixed(0)} GB`,
        layout: 'lvm',
        format: 'ext4',
        mountpoint: '/',
      });
    } else {
      configs.push({
        id: 'nvme-single',
        type: 'NVME',
        diskCount: 1,
        sizePerDisk: `${totalStorage} GB`,
        layout: 'single',
        format: 'ext4',
        mountpoint: '/',
      });
    }

    return configs;
  };

  const [formData, setFormData] = useState({
    projectId: '',
    deploymentName: '',
    operatingSystem: '',
    ipxeScriptUrl: '',
    sshKeyId: '',
    contractType: 'on_demand' as 'on_demand' | 'spot' | 'reserved',
    commitmentPeriod: '3_months' as '3_months' | '6_months' | '1_year',
    autoRenew: false,
    diskConfigs: generateDiskConfigs(),
    cloudInit: '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set default OS when loaded
  useEffect(() => {
    if (!formData.operatingSystem && defaultOS) {
      setFormData(prev => ({ ...prev, operatingSystem: defaultOS }));
    }
  }, [defaultOS, formData.operatingSystem]);

  // Auto-select default SSH key when keys are loaded
  useEffect(() => {
    if (!formData.sshKeyId && sshKeys.length > 0) {
      const defaultKey = sshKeys.find(key => key.is_default);
      if (defaultKey) {
        setFormData(prev => ({ ...prev, sshKeyId: defaultKey.id }));
      }
    }
  }, [sshKeys, formData.sshKeyId]);

  const selectedOS = osImages.find((os: OSImageResponse) => os.slug === formData.operatingSystem);
  const isIPXESelected = selectedOS?.isIpxe || formData.operatingSystem === 'ipxe-custom';

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (request: CreateOrderRequest) => marketplaceApi.createOrder(request),
    onError: (err: Error) => {
      setError(err.message || 'Failed to create order');
      setIsProcessingPayment(false);
    },
  });

  // Open Razorpay checkout
  const openRazorpayCheckout = async (orderData: CreateOrderResponse) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setError('Failed to load payment gateway. Please try again.');
      setIsProcessingPayment(false);
      return;
    }

    const options = {
      key: orderData.razorpayKeyId,
      amount: Math.round(orderData.amount * 100), // Convert INR to paise
      currency: orderData.currency,
      name: 'Podstack',
      description: `GPU Reservation - ${item.gpu.name}`,
      order_id: orderData.orderId,
      handler: async (response: any) => {
        // Payment successful - verify with backend
        try {
          const verifyResult = await marketplaceApi.verifyPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });

          if (verifyResult.success && verifyResult.reservation) {
            setVerifiedReservation(verifyResult.reservation);
            setShowProvisioningModal(true);
            queryClient.invalidateQueries({ queryKey: ['reservations'] });
            queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
            queryClient.invalidateQueries({ queryKey: ['marketplace-inventory'] });
            toast.success('Payment successful! Your instance is being provisioned.');
          } else {
            setError(verifyResult.message || 'Payment verification failed');
          }
        } catch (err: any) {
          setError(err.message || 'Payment verification failed');
        }
        setIsProcessingPayment(false);
      },
      modal: {
        ondismiss: () => {
          setIsProcessingPayment(false);
          toast.error('Payment cancelled');
        },
      },
      prefill: {
        email: localStorage.getItem('userEmail') || '',
      },
      theme: {
        color: '#4f46e5', // Primary color
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', (response: any) => {
      setError(`Payment failed: ${response.error.description}`);
      setIsProcessingPayment(false);
    });
    razorpay.open();
  };

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      projectService.create(data.name, data.description),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      const newProject = response.data?.data || response.data;
      if (newProject?.id) {
        setFormData(prev => ({ ...prev, projectId: newProject.id }));
        // Also update the global project selection
        setSelectedProject(newProject.id);
      }
      setShowCreateProject(false);
      setNewProjectName('');
      setNewProjectDescription('');
      toast.success('Project created successfully');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to create project');
    },
  });

  // Auto-select first project if none selected globally
  useEffect(() => {
    autoSelectFirstProject();
  }, [autoSelectFirstProject]);

  // Sync formData.projectId with global selectedProjectId
  useEffect(() => {
    if (selectedProjectId && formData.projectId !== selectedProjectId) {
      setFormData(prev => ({ ...prev, projectId: selectedProjectId }));
    }
  }, [selectedProjectId, formData.projectId]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    // Validate project selection
    if (!formData.projectId) {
      setError('Please select or create a project');
      return;
    }

    // Validate iPXE script URL if iPXE is selected
    if (isIPXESelected && !formData.ipxeScriptUrl) {
      setError('iPXE Script URL is required when using iPXE Custom');
      return;
    }

    if (isIPXESelected && formData.ipxeScriptUrl) {
      try {
        new URL(formData.ipxeScriptUrl);
      } catch {
        setError('Please enter a valid iPXE Script URL');
        return;
      }
    }

    // Show payment method selection modal
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async () => {
    setError(null);
    setShowPaymentModal(false);

    // Calculate duration based on contract type
    let durationHours = 168; // 1 week default
    if (formData.contractType === 'reserved') {
      const months = getCommitmentMonths();
      durationHours = months * 30 * 24; // Approximate
    }

    setIsProcessingPayment(true);

    try {
      // Create order with selected payment method
      const orderData = await createOrderMutation.mutateAsync({
        inventoryId: item.inventoryIds?.[0] || item.id,
        durationHours,
        sshKeyId: formData.sshKeyId || undefined,
        useSpot: formData.contractType === 'spot',
        projectId: formData.projectId,
      });

      await openRazorpayCheckout(orderData);
    } catch (err: any) {
      setError(err.message || 'Failed to create order');
      setIsProcessingPayment(false);
    }
  };

  const updateDiskConfig = (diskId: string, field: keyof DiskConfig, value: string) => {
    setFormData(prev => ({
      ...prev,
      diskConfigs: prev.diskConfigs.map(disk =>
        disk.id === diskId ? { ...disk, [field]: value } : disk
      ),
    }));
  };

  // Convert USD to INR with GST (fallback if backend not deployed)
  const GST_RATE = 1.18;
  const convertToINR = (usd: number) => usd * exchangeRate * GST_RATE;

  // Get INR prices with fallback to USD conversion
  // Define monthly and spot first (no dependencies)
  const getINRMonthly = () => {
    if (item.pricing.monthlyINR && item.pricing.monthlyINR > 0) return item.pricing.monthlyINR;
    if (item.pricing.monthlyUSD && item.pricing.monthlyUSD > 0) return convertToINR(item.pricing.monthlyUSD);
    return undefined;
  };
  const getINRSpot = () => {
    if (item.pricing.spotINR && item.pricing.spotINR > 0) return item.pricing.spotINR;
    if (item.pricing.spotUSD && item.pricing.spotUSD > 0) return convertToINR(item.pricing.spotUSD);
    return undefined;
  };
  // Hourly depends on monthly for reserved instances
  const getINRHourly = () => {
    // If hourlyINR is available and > 0, use it
    if (item.pricing.hourlyINR && item.pricing.hourlyINR > 0) {
      return item.pricing.hourlyINR;
    }
    // If hourlyUSD is available and > 0, convert it
    if (item.pricing.hourlyUSD && item.pricing.hourlyUSD > 0) {
      return convertToINR(item.pricing.hourlyUSD);
    }
    // For reserved instances, calculate hourly from monthly (730 hours/month)
    const monthlyINR = getINRMonthly();
    if (monthlyINR && monthlyINR > 0) {
      return monthlyINR / 730;
    }
    return 0;
  };

  // Calculate pricing based on contract type (using INR prices with GST included)
  const getHourlyRate = () => {
    const spotINR = getINRSpot();
    const monthlyINR = getINRMonthly();
    if (formData.contractType === 'spot' && spotINR) {
      return spotINR;
    }
    if (formData.contractType === 'reserved' && monthlyINR) {
      return monthlyINR / 730;
    }
    return getINRHourly();
  };

  const getCommitmentMonths = () => {
    switch (formData.commitmentPeriod) {
      case '3_months': return 3;
      case '6_months': return 6;
      case '1_year': return 12;
      default: return 3;
    }
  };

  const formatPrice = (price: number) => {
    if (price === undefined || price === null || isNaN(price)) return '₹--';
    return `₹${price.toFixed(2)}`;
  };

  const hourlyRate = getHourlyRate();
  const perCardHour = hourlyRate / item.configuration.gpuCount;
  const weeklyRate = hourlyRate * 168;
  const monthlyRate = getINRMonthly() || hourlyRate * 730;
  const commitmentMonths = getCommitmentMonths();
  const totalCommitmentCost = monthlyRate * commitmentMonths;

  const totalNvmeDisks = formData.diskConfigs.reduce((sum, d) => sum + d.diskCount, 0);
  const spotINR = getINRSpot();
  const monthlyINR = getINRMonthly();
  const hasSpot = typeof spotINR === 'number' && spotINR > 0;
  const hasReserved = typeof monthlyINR === 'number' && monthlyINR > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-4xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-800 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back To Inventory
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-800"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6">
              {/* GPU Title */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-6">
                {item.gpu.name}
              </h1>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {/* Left Column - Overview */}
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-4">Overview</h2>
                    <div className="bg-gray-800 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <CircleStackIcon className="w-4 h-4 text-blue-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Memory</p>
                            <p className="font-semibold text-white">
                              {item.configuration.ramGB >= 1024
                                ? `${(item.configuration.ramGB / 1024).toFixed(2)} TB`
                                : `${item.configuration.ramGB} GB`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                            <CircleStackIcon className="w-4 h-4 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">NVME Storage</p>
                            <p className="font-semibold text-white">
                              {item.configuration.storageGB >= 1024
                                ? `${(item.configuration.storageGB / 1024).toFixed(2)} TB`
                                : `${item.configuration.storageGB} GB`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                            <CircleStackIcon className="w-4 h-4 text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">NVME Disks</p>
                            <p className="font-semibold text-white">{totalNvmeDisks}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                            <CpuChipIcon className="w-4 h-4 text-green-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">CPU Cores</p>
                            <p className="font-semibold text-white">{item.configuration.cpuCores}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                            <ServerIcon className="w-4 h-4 text-orange-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">GPU</p>
                            <p className="font-semibold text-white text-sm">{item.gpu.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                            <ServerIcon className="w-4 h-4 text-primary-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">GPU Count</p>
                            <p className="font-semibold text-white">{item.configuration.gpuCount}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Configuration */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-4">Configure your device</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Project Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Project <span className="text-red-400">*</span>
                      </label>
                      {projectsLoading ? (
                        <div className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 animate-pulse text-sm text-gray-400">Loading...</div>
                      ) : showCreateProject ? (
                        <div className="border border-gray-700 rounded-lg p-4 bg-gray-800 space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">Project Name</label>
                            <input
                              type="text"
                              value={newProjectName}
                              onChange={(e) => setNewProjectName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-600 bg-gray-900 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="My Project"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">Description (optional)</label>
                            <input
                              type="text"
                              value={newProjectDescription}
                              onChange={(e) => setNewProjectDescription(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-600 bg-gray-900 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Project description"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateProject(false);
                                setNewProjectName('');
                                setNewProjectDescription('');
                              }}
                              className="flex-1 px-3 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (newProjectName.trim()) {
                                  createProjectMutation.mutate({
                                    name: newProjectName.trim(),
                                    description: newProjectDescription.trim(),
                                  });
                                }
                              }}
                              disabled={!newProjectName.trim() || createProjectMutation.isPending}
                              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:bg-gray-700 disabled:cursor-not-allowed"
                            >
                              {createProjectMutation.isPending ? 'Creating...' : 'Create'}
                            </button>
                          </div>
                        </div>
                      ) : projects.length > 0 ? (
                        <div className="space-y-2">
                          <select
                            value={formData.projectId}
                            onChange={(e) => {
                              const newProjectId = e.target.value;
                              setFormData({ ...formData, projectId: newProjectId });
                              // Also update the global project selection
                              setSelectedProject(newProjectId);
                            }}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            {projects.map((project: { id: string; name: string }) => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowCreateProject(true)}
                            className="flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-medium"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Create New Project
                          </button>
                        </div>
                      ) : (
                        <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                          <p className="text-sm text-gray-400 mb-3">No projects found. Create one to continue.</p>
                          <button
                            type="button"
                            onClick={() => setShowCreateProject(true)}
                            className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Create Project
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Deployment Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Deployment Name
                      </label>
                      <input
                        type="text"
                        value={formData.deploymentName}
                        onChange={(e) => setFormData({ ...formData, deploymentName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Add a name"
                      />
                    </div>

                    {/* Operating System */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Operating System
                      </label>
                      {osImagesLoading ? (
                        <div className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 animate-pulse text-sm text-gray-400">Loading...</div>
                      ) : osImages.length > 0 ? (
                        <select
                          value={formData.operatingSystem}
                          onChange={(e) => setFormData({ ...formData, operatingSystem: e.target.value, ipxeScriptUrl: '' })}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          {osImages.map((os: OSImageResponse) => (
                            <option key={os.slug} value={os.slug}>
                              {os.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={formData.operatingSystem}
                          onChange={(e) => setFormData({ ...formData, operatingSystem: e.target.value, ipxeScriptUrl: '' })}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="ubuntu-noble-vanilla">Ubuntu Noble Vanilla</option>
                          <option value="ubuntu-jammy-hpc">Ubuntu Jammy HPC</option>
                          <option value="ubuntu-noble-hpc">Ubuntu Noble HPC</option>
                          <option value="ubuntu-jammy-vanilla">Ubuntu Jammy Vanilla</option>
                          <option value="ipxe-custom">iPXE Custom</option>
                        </select>
                      )}
                      {selectedOS?.description && (
                        <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                          <InformationCircleIcon className="w-3 h-3" />
                          {selectedOS.description}
                        </p>
                      )}
                    </div>

                    {/* iPXE Script URL */}
                    {isIPXESelected && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          iPXE Script URL <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="url"
                          value={formData.ipxeScriptUrl}
                          onChange={(e) => setFormData({ ...formData, ipxeScriptUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="https://example.com/boot.ipxe"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          Enter the URL to your custom iPXE boot script
                        </p>
                      </div>
                    )}

                    {/* SSH Keys */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        SSH Keys
                      </label>
                      {sshKeysLoading ? (
                        <div className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 animate-pulse text-sm text-gray-400">Loading...</div>
                      ) : sshKeys.length > 0 ? (
                        <div className="space-y-2">
                          <div className="border border-gray-700 rounded-lg divide-y divide-gray-700 max-h-40 overflow-y-auto">
                            {sshKeys.map((key) => (
                              <label
                                key={key.id}
                                className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-800 transition-colors ${
                                  formData.sshKeyId === key.id ? 'bg-primary-500/10' : ''
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="sshKey"
                                  value={key.id}
                                  checked={formData.sshKeyId === key.id}
                                  onChange={() => setFormData({ ...formData, sshKeyId: key.id })}
                                  className="w-4 h-4 text-primary-500 border-gray-600 focus:ring-primary-500 bg-gray-800"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <KeyIcon className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium text-white text-sm truncate">{key.name}</span>
                                    {key.is_default && (
                                      <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Default</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 truncate font-mono">{key.fingerprint}</p>
                                </div>
                                {formData.sshKeyId === key.id && (
                                  <CheckIcon className="w-5 h-5 text-primary-400 flex-shrink-0" />
                                )}
                              </label>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowCreateSSHKeyModal(true)}
                            className="flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-medium"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Another Key
                          </button>
                        </div>
                      ) : (
                        <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-gray-700 rounded-lg">
                              <KeyIcon className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-300">No SSH keys found</p>
                              <p className="text-xs text-gray-400">Add a key to access your instance</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowCreateSSHKeyModal(true)}
                            className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Create SSH Key
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Contract Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Contract Type
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, contractType: 'on_demand' })}
                          className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                            formData.contractType === 'on_demand'
                              ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                              : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                          }`}
                        >
                          On Demand
                        </button>
                        {hasSpot && (
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, contractType: 'spot' })}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                              formData.contractType === 'spot'
                                ? 'border-green-500 bg-green-500/10 text-green-400'
                                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            Spot
                          </button>
                        )}
                        {hasReserved && (
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, contractType: 'reserved' })}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                              formData.contractType === 'reserved'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            Reserved
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Commitment Period - Only shown for Reserved */}
                    {formData.contractType === 'reserved' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Commitment Period
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, commitmentPeriod: '3_months' })}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                              formData.commitmentPeriod === '3_months'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            3 Months
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, commitmentPeriod: '6_months' })}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                              formData.commitmentPeriod === '6_months'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            6 Months
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, commitmentPeriod: '1_year' })}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                              formData.commitmentPeriod === '1_year'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            1 Year
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-400">
                          Longer commitments may offer better pricing
                        </p>
                      </div>
                    )}

                    {/* Advanced Options Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium"
                    >
                      {showAdvanced ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                      {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
                    </button>

                    {showAdvanced && (
                      <div className="space-y-6 pt-2">
                        {/* Disk Layouts */}
                        <div>
                          <h3 className="text-sm font-semibold text-white mb-1">Select Disk Layouts</h3>
                          <p className="text-xs text-gray-400 mb-4">Choose one layout for each disk type</p>

                          <div className="space-y-4">
                            {formData.diskConfigs.map((disk) => (
                              <div key={disk.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                                <div className="flex items-center gap-2 mb-3">
                                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">
                                    {disk.type}
                                  </span>
                                  <span className="text-sm text-gray-400">
                                    Disk Count: <span className="font-medium text-white">{disk.diskCount}</span>
                                  </span>
                                  <span className="text-sm text-gray-400">
                                    Size per disk: <span className="font-medium text-white">{disk.sizePerDisk}</span>
                                  </span>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">Layout</label>
                                    <select
                                      value={disk.layout}
                                      onChange={(e) => updateDiskConfig(disk.id, 'layout', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-600 bg-gray-900 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                                    >
                                      {diskLayouts.map((layout) => (
                                        <option key={layout} value={layout}>{layout}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">Format</label>
                                    <select
                                      value={disk.format}
                                      onChange={(e) => updateDiskConfig(disk.id, 'format', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-600 bg-gray-900 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                                    >
                                      {diskFormats.map((format) => (
                                        <option key={format} value={format}>{format}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">Mountpoint</label>
                                    <input
                                      type="text"
                                      value={disk.mountpoint}
                                      onChange={(e) => updateDiskConfig(disk.id, 'mountpoint', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-600 bg-gray-900 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Cloud Init */}
                        <div>
                          <h3 className="text-sm font-semibold text-white mb-1">Cloud Init</h3>
                          <p className="text-xs text-gray-400 mb-2">Enter your cloud-init configuration below.</p>
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                            <p className="text-xs text-amber-300">
                              The following properties are not supported and will be ignored:
                            </p>
                            <ul className="text-xs text-amber-400/80 mt-1 ml-4 list-disc">
                              <li>disk_setup, fs_setup, device_aliases</li>
                              <li>datasource, network-config, network-interfaces</li>
                            </ul>
                          </div>
                          <textarea
                            value={formData.cloudInit}
                            onChange={(e) => setFormData({ ...formData, cloudInit: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                            rows={6}
                            placeholder={`#cloud-config\npackages:\n  - nginx\n  - docker.io`}
                          />
                        </div>

                        {/* Auto-renew */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.autoRenew}
                            onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                            className="w-4 h-4 rounded text-primary-500 border-gray-600 focus:ring-primary-500 bg-gray-800"
                          />
                          <span className="text-sm text-gray-300">Auto-renew reservation</span>
                        </label>
                      </div>
                    )}

                    {/* Error */}
                    {error && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        {error}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Pricing & Action */}
          <div className="border-t border-gray-800 bg-gray-800/50 p-4 sm:p-6 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Pricing */}
              <div className="space-y-1">
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    formData.contractType === 'on_demand'
                      ? 'bg-primary-500/20 text-primary-400'
                      : formData.contractType === 'spot'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {formData.contractType === 'on_demand' ? 'On Demand' : formData.contractType === 'spot' ? 'Spot' : 'Reserved'}
                  </span>
                  {formData.contractType === 'reserved' && (
                    <span className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded text-xs font-medium">
                      {formData.commitmentPeriod === '3_months' ? '3 Months' : formData.commitmentPeriod === '6_months' ? '6 Months' : '1 Year'}
                    </span>
                  )}
                </div>

                {formData.contractType === 'reserved' ? (
                  <>
                    <p className="text-2xl font-bold text-white">
                      {formatPrice(monthlyRate)}
                      <span className="text-sm font-normal text-gray-400"> /month</span>
                    </p>
                    <p className="text-lg text-gray-400">
                      {formatPrice(totalCommitmentCost)}
                      <span className="text-sm text-gray-500"> total for {commitmentMonths} months</span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-white">
                      {formatPrice(perCardHour)}
                      <span className="text-sm font-normal text-gray-400"> per card-hour</span>
                    </p>
                    <p className="text-lg text-gray-400">
                      {formatPrice(weeklyRate)}
                      <span className="text-sm text-gray-500"> per week</span>
                    </p>
                  </>
                )}

                <p className="text-xs text-gray-500 mt-2 max-w-md">
                  {formData.contractType === 'reserved'
                    ? '*Reserved instances require commitment for the selected period. Early cancellation may incur fees.'
                    : '*If you are not happy or decide to cancel earlier, we will process a refund for the unused portion of the billing cycle'
                  }
                </p>
              </div>

              {/* Submit Button */}
              <button
                onClick={() => handleSubmit()}
                disabled={isProcessingPayment || createOrderMutation.isPending}
                className="w-full sm:w-auto px-8 py-3 text-base font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessingPayment ? 'Processing Payment...' : 'Proceed to Checkout'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SSH Key Creation Modal */}
      <CreateSSHKeyModal
        isOpen={showCreateSSHKeyModal}
        onClose={() => setShowCreateSSHKeyModal(false)}
        onKeyCreated={(key) => {
          // Auto-select the newly created key
          setFormData({ ...formData, sshKeyId: key.id });
          setShowCreateSSHKeyModal(false);
        }}
      />

      {/* Provisioning Modal - shown after successful payment */}
      {showProvisioningModal && verifiedReservation && (
        <ProvisioningModal
          reservation={verifiedReservation}
          onClose={() => {
            setShowProvisioningModal(false);
            onSuccess?.();
            onClose();
          }}
          onViewReservations={() => {
            setShowProvisioningModal(false);
            onSuccess?.();
            onClose();
            // Navigate to reservations page
            window.location.href = '/baremetal/reservations';
          }}
        />
      )}

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-white">
                Select Payment Method
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-2 text-gray-400 hover:text-gray-300 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {/* Razorpay (UPI, cards, net banking) */}
              <>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                  <p className="text-sm text-blue-400">
                    Pay securely via Razorpay. Supports UPI, Cards, Net Banking, and Wallets.
                  </p>
                </div>
                <div className="mb-4 p-4 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-400">Amount to Pay:</span>
                    <span className="text-xl font-bold text-white">
                      {formatPrice(formData.contractType === 'reserved' ? totalCommitmentCost : weeklyRate)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.contractType === 'reserved'
                      ? `For ${commitmentMonths} month commitment`
                      : 'For 1 week'}
                  </p>
                </div>
                <button
                  onClick={handlePaymentConfirm}
                  disabled={isProcessingPayment}
                  className="btn-primary w-full py-3 text-base disabled:opacity-50"
                >
                  {isProcessingPayment ? 'Processing...' : `Pay with Razorpay`}
                </button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Secure payment powered by Razorpay
                </p>
              </>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationModal;
